import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Briefcase, CheckCircle, Users, Plus, X, Edit, Eye, EyeOff } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';

interface JobsData {
    id: number;
    slate_id_fk: number;
    title: string;
    status: number;
    scraped: string | null;
    created_at: string;
    updated_at: string;
    vacancies: number;
    salary_type: number;
    min_salary: number;
    max_salary: number;
    jobType: number;
    description: string;
    exp_level: number;
    min_exp: number;
    max_exp: number;
    deadline: string;
    is_verified: number;
    urgentHiring: number;
    jobCategory: {
        id: number;
        name: string;
    } | null;
    teachingCategory: {
        id: number;
        name: string;
    } | null;
    teachingLevel: {
        id: number;
        name: string;
    } | null;
    counts: {
        applications: number;
        shortlisted: number;
        rejected: number;
        views: number;
    };
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

interface Category {
    id: number;
    name: string;
}

interface Board {
    board_id: number;
    board_name: string;
}

interface PostJobFormData {
    title: string;
    jobCategoryId: number | '';
    teachingCategoryIds: number[];
    teachinglevel: number | '';
    board: number | '';
    vacancies: number | '';
    salary_type: number;
    min_salary: number | '';
    max_salary: number | '';
    jobType: number | '';
    description: string;
    exp_level: number;
    min_exp: number | '';
    max_exp: number | '';
    deadline: string;
    is_verified: number;
    urgentHiring: number;
    status: number;
}

// Tab configuration for Post Job modal
const JOB_FORM_TABS = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'salary', label: 'Salary & Type' },
    { id: 'experience', label: 'Experience' },
    { id: 'description', label: 'Description' },
];

const JobList: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const schoolId = id ? parseInt(id) : null;

    const [jobs, setJobs] = useState<JobsData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
    });
    const [sortColumn, setSortColumn] = useState('created_at');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
    const [statusFilter, setStatusFilter] = useState<string>('');

    // Job Detail Modal state
    const [selectedJob, setSelectedJob] = useState<JobsData | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [, setIsLoadingDetails] = useState(false);

    // Post/Edit Job Modal state
    const [isPostJobModalOpen, setIsPostJobModalOpen] = useState(false);
    const [isEditJobModalOpen, setIsEditJobModalOpen] = useState(false);
    const [editJobId, setEditJobId] = useState<number | null>(null);
    const [isSubmittingJob, setIsSubmittingJob] = useState(false);
    const [isLoadingEditJob, setIsLoadingEditJob] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');

    // Dropdown data
    const [categories, setCategories] = useState<Category[]>([]);
    const [teachingCategories, setTeachingCategories] = useState<Category[]>([]);
    const [teachingLevels, setTeachingLevels] = useState<Category[]>([]);
    const [boards, setBoards] = useState<Board[]>([]);

    // Form Data
    const initialFormData: PostJobFormData = {
        title: '',
        jobCategoryId: '',
        teachingCategoryIds: [],
        teachinglevel: '',
        board: '',
        vacancies: '',
        salary_type: 3,
        min_salary: '',
        max_salary: '',
        jobType: '',
        description: '',
        exp_level: 1,
        min_exp: '',
        max_exp: '',
        deadline: '',
        is_verified: 0,
        urgentHiring: 0,
        status: 1,
    };

    const [formData, setFormData] = useState<PostJobFormData>(initialFormData);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Fetch jobs for this school
    const fetchJobs = async (page: number = pagination.currentPage) => {
        if (!schoolId) {
            showToast('School ID is required', 'error');
            return;
        }

        try {
            setIsLoading(true);

            const payload: any = {
                page,
                limit: pagination.itemsPerPage,
                sort_column: sortColumn,
                sort_order: sortDirection,
                school_id: schoolId,
            };

            if (searchTerm && searchTerm.trim() !== '') {
                payload.searchTerm = searchTerm.trim();
            }

            if (statusFilter !== '') {
                payload.status_filter = parseInt(statusFilter);
            }

            const response = await apiRequest(`/api/jobs/school/${schoolId}`, {
            method: 'POST',
            body: JSON.stringify(payload),
});

            if (response.success) {
                setJobs(response.data.jobs || []);
                setPagination({
                    currentPage: response.data.pagination?.currentPage || page,
                    totalPages: response.data.pagination?.totalPages || 1,
                    totalItems: response.data.pagination?.totalItems || 0,
                    itemsPerPage: response.data.pagination?.itemsPerPage || 25,
                });
            } else {
                showToast('Failed to fetch jobs', 'error');
                setJobs([]);
                setPagination({
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: 0,
                    itemsPerPage: 25,
                });
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
            showToast('Failed to fetch jobs', 'error');
            setJobs([]);
            setPagination({
                currentPage: 1,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: 25,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch dropdown data
    const fetchCategories = async () => {
        try {
            const response = await apiRequest('/api/jobs/categories', { method: 'GET' });
            if (response.success) {
                setCategories(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchTeachingCategories = async (categoryId: number) => {
        try {
            const response = await apiRequest(`/api/jobs/teaching_categories/${categoryId}`, { method: 'GET' });
            if (response.success) {
                setTeachingCategories(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching teaching categories:', error);
        }
    };

    const fetchTeachingLevels = async () => {
        try {
            const response = await apiRequest('/api/jobs/levels', { method: 'GET' });
            if (response.success) {
                setTeachingLevels(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching teaching levels:', error);
        }
    };

    const fetchBoards = async () => {
        try {
            const response = await apiRequest('/api/jobs/boards', { method: 'GET' });
            if (response.success) {
                setBoards(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching boards:', error);
        }
    };

    // Fetch job for editing
    const fetchJobForEdit = async (jobId: number) => {
        try {
            setIsLoadingEditJob(true);
            const response = await apiRequest(`/api/jobs/${jobId}`, { method: 'GET' });

            if (response.success && response.data) {
                const job = response.data;
                setFormData({
                    title: job.title || '',
                    jobCategoryId: job.jobCategoryId || '',
                    teachingCategoryIds: job.teachingCategoryIds || [],
                    teachinglevel: job.teachingLevelId || '',
                    board: job.board || '',
                    vacancies: job.vacancies || '',
                    salary_type: job.salary_type || 3,
                    min_salary: job.min_salary || '',
                    max_salary: job.max_salary || '',
                    jobType: job.jobType || '',
                    description: job.description || '',
                    exp_level: job.exp_level || 1,
                    min_exp: job.min_exp || '',
                    max_exp: job.max_exp || '',
                    deadline: job.deadline ? job.deadline.split('T')[0] : '',
                    is_verified: job.is_verified ?? 0,
                    urgentHiring: job.urgentHiring ?? 0,
                    status: job.status ?? 1,
                });

                if (job.jobCategoryId === 22 || job.jobCategoryId === 23) {
                    await fetchTeachingCategories(job.jobCategoryId);
                }
            } else {
                showToast('Failed to fetch job details', 'error');
                setIsEditJobModalOpen(false);
            }
        } catch (error) {
            console.error('Error fetching job for edit:', error);
            showToast('Failed to fetch job details', 'error');
            setIsEditJobModalOpen(false);
        } finally {
            setIsLoadingEditJob(false);
        }
    };

    // Load jobs on mount and when filters change
    useEffect(() => {
        if (schoolId) {
            fetchJobs(1);
        }
    }, [sortColumn, sortDirection, statusFilter, schoolId]);

    // Debounce search
    useEffect(() => {
        if (!schoolId) return;

        const delayDebounce = setTimeout(() => {
            fetchJobs(1);
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    // Load dropdown data when modal opens
    useEffect(() => {
        if (isPostJobModalOpen || isEditJobModalOpen) {
            fetchCategories();
            fetchTeachingLevels();
            fetchBoards();
            setActiveTab('basic');
        }
    }, [isPostJobModalOpen, isEditJobModalOpen]);

    // Fetch teaching categories when job category changes
    useEffect(() => {
        if (formData.jobCategoryId && (formData.jobCategoryId === 22 || formData.jobCategoryId === 23)) {
            fetchTeachingCategories(formData.jobCategoryId);
        } else {
            setTeachingCategories([]);
        }
    }, [formData.jobCategoryId]);

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchJobs(newPage);
        }
    };

    // Handle sort
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
    };

    // Handle job click
    const handleJobClick = async (job: JobsData) => {
        setSelectedJob(job);
        setIsModalOpen(true);
        setIsLoadingDetails(true);

        try {
            const response = await apiRequest(`/api/jobs/${job.id}`, { method: 'GET' });
            if (response.success) {
                setSelectedJob(response.data);
            }
        } catch (error) {
            console.error('Error fetching job details:', error);
        } finally {
            setIsLoadingDetails(false);
        }
    };

    // Handle modal close
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedJob(null);
    };

    const handleClosePostJobModal = () => {
        setIsPostJobModalOpen(false);
        setFormData(initialFormData);
        setFormErrors({});
        setActiveTab('basic');
    };

    // Handle Edit Job modal
    const handleOpenEditJobModal = (jobId: number, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setEditJobId(jobId);
        setFormData(initialFormData);
        setFormErrors({});
        setActiveTab('basic');
        setIsEditJobModalOpen(true);
        fetchJobForEdit(jobId);
    };

    const handleCloseEditJobModal = () => {
        setIsEditJobModalOpen(false);
        setEditJobId(null);
        setFormData(initialFormData);
        setFormErrors({});
        setTeachingCategories([]);
        setActiveTab('basic');
    };

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
        } else if (type === 'number') {
            setFormData(prev => ({ ...prev, [name]: value === '' ? '' : parseInt(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle teaching category change
    const handleTeachingCategoryChange = (categoryId: number) => {
        setFormData(prev => {
            const currentIds = prev.teachingCategoryIds;
            if (currentIds.includes(categoryId)) {
                return { ...prev, teachingCategoryIds: currentIds.filter(id => id !== categoryId) };
            } else {
                return { ...prev, teachingCategoryIds: [...currentIds, categoryId] };
            }
        });

        if (formErrors.teachingCategoryIds) {
            setFormErrors(prev => ({ ...prev, teachingCategoryIds: '' }));
        }
    };

    // Validate form by tab
    const validateTab = (tabId: string): boolean => {
        const errors: Record<string, string> = {};

        if (tabId === 'basic') {
            if (!formData.title.trim()) {
                errors.title = 'Job title is required';
            } else if (formData.title.length < 5) {
                errors.title = 'Title must be at least 5 characters';
            }

            if (!formData.jobCategoryId) {
                errors.jobCategoryId = 'Category is required';
            }

            if (formData.jobCategoryId === 22 || formData.jobCategoryId === 23) {
                if (formData.teachingCategoryIds.length === 0) {
                    errors.teachingCategoryIds = 'Please select at least one teaching category';
                }
            }

            if (formData.jobCategoryId === 22) {
                if (!formData.teachinglevel) errors.teachinglevel = 'Teaching level is required';
                if (!formData.board) errors.board = 'Board is required';
            }
        }

        if (tabId === 'salary') {
            if (!formData.jobType) {
                errors.jobType = 'Job type is required';
            }

            if (formData.salary_type === 1 && !formData.min_salary) {
                errors.min_salary = 'Salary is required';
            }

            if (formData.salary_type === 2) {
                if (!formData.min_salary) errors.min_salary = 'Min salary is required';
                if (!formData.max_salary) errors.max_salary = 'Max salary is required';
                if (formData.min_salary && formData.max_salary && Number(formData.max_salary) <= Number(formData.min_salary)) {
                    errors.max_salary = 'Max salary must be greater than min salary';
                }
            }
        }

        if (tabId === 'experience') {
            if (formData.exp_level === 2 && !formData.min_exp) {
                errors.min_exp = 'Experience is required';
            }

            if (formData.exp_level === 3) {
                if (!formData.min_exp) errors.min_exp = 'Min experience is required';
                if (!formData.max_exp) errors.max_exp = 'Max experience is required';
                if (formData.min_exp && formData.max_exp && Number(formData.max_exp) <= Number(formData.min_exp)) {
                    errors.max_exp = 'Max experience must be greater than min experience';
                }
            }
        }

        if (tabId === 'description') {
            if (!formData.description.trim()) {
                errors.description = 'Job description is required';
            }
        }

        setFormErrors(prev => ({ ...prev, ...errors }));
        return Object.keys(errors).length === 0;
    };

    // Validate all tabs
    const validateForm = (): boolean => {
        let isValid = true;
        const allErrors: Record<string, string> = {};

        // Validate basic tab
        if (!formData.title.trim()) {
            allErrors.title = 'Job title is required';
            isValid = false;
        } else if (formData.title.length < 5) {
            allErrors.title = 'Title must be at least 5 characters';
            isValid = false;
        }

        if (!formData.jobCategoryId) {
            allErrors.jobCategoryId = 'Category is required';
            isValid = false;
        }

        if (formData.jobCategoryId === 22 || formData.jobCategoryId === 23) {
            if (formData.teachingCategoryIds.length === 0) {
                allErrors.teachingCategoryIds = 'Please select at least one teaching category';
                isValid = false;
            }
        }

        if (formData.jobCategoryId === 22) {
            if (!formData.teachinglevel) {
                allErrors.teachinglevel = 'Teaching level is required';
                isValid = false;
            }
            if (!formData.board) {
                allErrors.board = 'Board is required';
                isValid = false;
            }
        }

        // Validate salary tab
        if (!formData.jobType) {
            allErrors.jobType = 'Job type is required';
            isValid = false;
        }

        if (formData.salary_type === 1 && !formData.min_salary) {
            allErrors.min_salary = 'Salary is required';
            isValid = false;
        }

        if (formData.salary_type === 2) {
            if (!formData.min_salary) {
                allErrors.min_salary = 'Min salary is required';
                isValid = false;
            }
            if (!formData.max_salary) {
                allErrors.max_salary = 'Max salary is required';
                isValid = false;
            }
            if (formData.min_salary && formData.max_salary && Number(formData.max_salary) <= Number(formData.min_salary)) {
                allErrors.max_salary = 'Max salary must be greater than min salary';
                isValid = false;
            }
        }

        // Validate experience tab
        if (formData.exp_level === 2 && !formData.min_exp) {
            allErrors.min_exp = 'Experience is required';
            isValid = false;
        }

        if (formData.exp_level === 3) {
            if (!formData.min_exp) {
                allErrors.min_exp = 'Min experience is required';
                isValid = false;
            }
            if (!formData.max_exp) {
                allErrors.max_exp = 'Max experience is required';
                isValid = false;
            }
            if (formData.min_exp && formData.max_exp && Number(formData.max_exp) <= Number(formData.min_exp)) {
                allErrors.max_exp = 'Max experience must be greater than min experience';
                isValid = false;
            }
        }

        // Validate description tab
        if (!formData.description.trim()) {
            allErrors.description = 'Job description is required';
            isValid = false;
        }

        setFormErrors(allErrors);
        return isValid;
    };

    // Handle next tab
    const handleNextTab = () => {
        const currentIndex = JOB_FORM_TABS.findIndex(tab => tab.id === activeTab);
        if (validateTab(activeTab) && currentIndex < JOB_FORM_TABS.length - 1) {
            setActiveTab(JOB_FORM_TABS[currentIndex + 1].id);
        }
    };

    // Handle previous tab
    const handlePrevTab = () => {
        const currentIndex = JOB_FORM_TABS.findIndex(tab => tab.id === activeTab);
        if (currentIndex > 0) {
            setActiveTab(JOB_FORM_TABS[currentIndex - 1].id);
        }
    };

    // Submit Post Job
    const handleSubmitJob = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            // Find first tab with errors and switch to it
            const errorFields = Object.keys(formErrors);
            if (errorFields.includes('title') || errorFields.includes('jobCategoryId') || errorFields.includes('teachingCategoryIds') || errorFields.includes('teachinglevel') || errorFields.includes('board')) {
                setActiveTab('basic');
            } else if (errorFields.includes('jobType') || errorFields.includes('min_salary') || errorFields.includes('max_salary')) {
                setActiveTab('salary');
            } else if (errorFields.includes('min_exp') || errorFields.includes('max_exp')) {
                setActiveTab('experience');
            } else if (errorFields.includes('description')) {
                setActiveTab('description');
            }
            showToast('Please fix the form errors', 'error');
            return;
        }

        if (!schoolId) {
            showToast('School ID is required', 'error');
            return;
        }

        setIsSubmittingJob(true);

        try {
            const payload = {
                title: formData.title,
                jobCategoryId: formData.jobCategoryId,
                teachingCategoryIds: formData.teachingCategoryIds,
                teachinglevel: formData.teachinglevel || 0,
                board: formData.board || 0,
                vacancies: formData.vacancies || 0,
                salary_type: formData.salary_type,
                min_salary: formData.min_salary || 0,
                max_salary: formData.max_salary || 0,
                jobType: formData.jobType || 0,
                description: formData.description,
                exp_level: formData.exp_level,
                min_exp: formData.min_exp || 0,
                max_exp: formData.max_exp || 0,
                deadline: formData.deadline || null,
                slate_id_fk: schoolId,
                is_verified: formData.is_verified,
                urgentHiring: formData.urgentHiring,
            };

            const response = await apiRequest('/api/jobs/craete_job', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (response.success) {
                showToast('Job posted successfully!', 'success');
                handleClosePostJobModal();
                fetchJobs(1);
            } else {
                showToast(response.message || 'Failed to post job', 'error');
            }
        } catch (error) {
            console.error('Error posting job:', error);
            showToast('Failed to post job', 'error');
        } finally {
            setIsSubmittingJob(false);
        }
    };

    // Submit Edit Job
    const handleSubmitEditJob = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast('Please fix the form errors', 'error');
            return;
        }

        if (!editJobId) {
            showToast('Job ID not found', 'error');
            return;
        }

        setIsSubmittingJob(true);

        try {
            const payload = {
                title: formData.title,
                jobCategoryId: formData.jobCategoryId,
                teachingCategoryIds: formData.teachingCategoryIds,
                teachinglevel: formData.teachinglevel || 0,
                board: formData.board || 0,
                vacancies: formData.vacancies || 0,
                salary_type: formData.salary_type,
                min_salary: formData.min_salary || 0,
                max_salary: formData.max_salary || 0,
                jobType: formData.jobType || 0,
                description: formData.description,
                exp_level: formData.exp_level,
                min_exp: formData.min_exp || 0,
                max_exp: formData.max_exp || 0,
                deadline: formData.deadline || null,
                is_verified: formData.is_verified,
                urgentHiring: formData.urgentHiring,
                status: formData.status,
            };

            const response = await apiRequest(`/api/jobs/update_job/${editJobId}`, {
                method: 'PUT',
                body: JSON.stringify(payload),
            });

            if (response.success) {
                showToast('Job updated successfully!', 'success');
                handleCloseEditJobModal();
                fetchJobs(pagination.currentPage);
            } else {
                showToast(response.message || 'Failed to update job', 'error');
            }
        } catch (error) {
            console.error('Error updating job:', error);
            showToast('Failed to update job', 'error');
        } finally {
            setIsSubmittingJob(false);
        }
    };

    // Handle status toggle
    const handleToggleStatus = async (status: number) => {
        if (!selectedJob) return;

        try {
            const newStatus = status === 1 ? 0 : 1;
            const response = await apiRequest(`/api/jobs/${selectedJob.id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status: newStatus }),
            });

            if (response.success) {
                showToast(`Job ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`, 'success');
                setSelectedJob({ ...selectedJob, status: newStatus });
                fetchJobs(pagination.currentPage);
            } else {
                showToast('Failed to update job status', 'error');
            }
        } catch (error) {
            console.error('Error updating job status:', error);
            showToast('Failed to update job status', 'error');
        }
    };

    // Get status badge
    const getStatusBadge = (status: number) => {
        return status === 1 ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                <Eye size={12} />
                Active
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                <EyeOff size={12} />
                Inactive
            </span>
        );
    };

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(/am/i, 'AM').replace(/pm/i, 'PM');
        return `${dateStr}, ${timeStr}`;
    };

    // Truncate text
    const truncateText = (text: string, maxLength: number = 80) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Get salary display
    const getSalaryDisplay = (job: JobsData) => {
        if (job.salary_type === 3) return 'Negotiable';
        if (job.salary_type === 1) return `₹${job.min_salary?.toLocaleString()}`;
        if (job.salary_type === 2) return `₹${job.min_salary?.toLocaleString()} - ₹${job.max_salary?.toLocaleString()}`;
        return '-';
    };

    // Get job type display
    const getJobTypeDisplay = (jobType: number) => {
        if (jobType === 1) return 'Full Time';
        if (jobType === 2) return 'Part Time';
        return '-';
    };

    // Check if tab has errors
    const tabHasErrors = (tabId: string): boolean => {
        const errorFields = Object.keys(formErrors);
        if (tabId === 'basic') {
            return errorFields.some(f => ['title', 'jobCategoryId', 'teachingCategoryIds', 'teachinglevel', 'board'].includes(f));
        }
        if (tabId === 'salary') {
            return errorFields.some(f => ['jobType', 'min_salary', 'max_salary'].includes(f));
        }
        if (tabId === 'experience') {
            return errorFields.some(f => ['min_exp', 'max_exp'].includes(f));
        }
        if (tabId === 'description') {
            return errorFields.some(f => ['description'].includes(f));
        }
        return false;
    };

    // Render Tab Content
    const renderTabContent = (isEdit: boolean = false) => {
        const showTeachingFields = formData.jobCategoryId === 22 || formData.jobCategoryId === 23;
        const showTeachingLevelAndBoard = formData.jobCategoryId === 22;

        switch (activeTab) {
            case 'basic':
                return (
                    <div className="space-y-6">
                        {/* Job Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Job Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="We are hiring CBSE teachers"
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.title ? 'border-red-500' : 'border-gray-300'}`}
                            />
                            {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
                        </div>

                        {/* Category */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Category <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="jobCategoryId"
                                value={formData.jobCategoryId}
                                onChange={(e) => {
                                    handleInputChange(e);
                                    setFormData(prev => ({ ...prev, jobCategoryId: parseInt(e.target.value) || '', teachingCategoryIds: [] }));
                                }}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.jobCategoryId ? 'border-red-500' : 'border-gray-300'}`}
                            >
                                <option value="">Select a Category</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                            {formErrors.jobCategoryId && <p className="text-red-500 text-xs mt-1">{formErrors.jobCategoryId}</p>}
                        </div>

                        {/* Teaching Categories */}
                        {showTeachingFields && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Teaching Category <span className="text-red-500">*</span>
                                </label>
                                <div className={`border rounded-md p-2 max-h-40 overflow-y-auto ${formErrors.teachingCategoryIds ? 'border-red-500' : 'border-gray-300'}`}>
                                    {teachingCategories.length === 0 ? (
                                        <p className="text-sm text-gray-500 py-2">Loading categories...</p>
                                    ) : (
                                        teachingCategories.map((cat) => (
                                            <label key={cat.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.teachingCategoryIds.includes(cat.id)}
                                                    onChange={() => handleTeachingCategoryChange(cat.id)}
                                                    className="rounded border-gray-300"
                                                />
                                                <span className="text-sm text-gray-700">{cat.name}</span>
                                            </label>
                                        ))
                                    )}
                                </div>
                                {formData.teachingCategoryIds.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {formData.teachingCategoryIds.map((catId) => {
                                            const cat = teachingCategories.find(c => c.id === catId);
                                            return cat ? (
                                                <span key={catId} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                    {cat.name}
                                                    <X size={12} className="cursor-pointer" onClick={() => handleTeachingCategoryChange(catId)} />
                                                </span>
                                            ) : null;
                                        })}
                                    </div>
                                )}
                                {formErrors.teachingCategoryIds && <p className="text-red-500 text-xs mt-1">{formErrors.teachingCategoryIds}</p>}
                            </div>
                        )}

                        {/* Teaching Level and Board */}
                        {showTeachingLevelAndBoard && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Teaching Level <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="teachinglevel"
                                        value={formData.teachinglevel}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.teachinglevel ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Select a Level</option>
                                        {teachingLevels.map((level) => (
                                            <option key={level.id} value={level.id}>{level.name}</option>
                                        ))}
                                    </select>
                                    {formErrors.teachinglevel && <p className="text-red-500 text-xs mt-1">{formErrors.teachinglevel}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Board <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="board"
                                        value={formData.board}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.board ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Select a Board</option>
                                        {boards.map((board) => (
                                            <option key={board.board_id} value={board.board_id}>{board.board_name}</option>
                                        ))}
                                    </select>
                                    {formErrors.board && <p className="text-red-500 text-xs mt-1">{formErrors.board}</p>}
                                </div>
                            </div>
                        )}

                        {/* Vacancies and Deadline */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Vacancies</label>
                                <input
                                    type="number"
                                    name="vacancies"
                                    value={formData.vacancies}
                                    onChange={handleInputChange}
                                    placeholder="5"
                                    min="0"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                                <input
                                    type="date"
                                    name="deadline"
                                    value={formData.deadline}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'salary':
                return (
                    <div className="space-y-6">
                        {/* Job Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Job Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="jobType"
                                value={formData.jobType}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.jobType ? 'border-red-500' : 'border-gray-300'}`}
                            >
                                <option value="">Select Job Type</option>
                                <option value={1}>Full Time</option>
                                <option value={2}>Part Time</option>
                            </select>
                            {formErrors.jobType && <p className="text-red-500 text-xs mt-1">{formErrors.jobType}</p>}
                        </div>

                        {/* Salary Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Salary Type</label>
                            <div className="flex gap-4 mb-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="salary_type"
                                        value={1}
                                        checked={formData.salary_type === 1}
                                        onChange={() => setFormData(prev => ({ ...prev, salary_type: 1 }))}
                                        className="text-blue-600"
                                    />
                                    <span className="text-sm">Fixed Salary</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="salary_type"
                                        value={2}
                                        checked={formData.salary_type === 2}
                                        onChange={() => setFormData(prev => ({ ...prev, salary_type: 2 }))}
                                        className="text-blue-600"
                                    />
                                    <span className="text-sm">Salary Range</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="salary_type"
                                        value={3}
                                        checked={formData.salary_type === 3}
                                        onChange={() => setFormData(prev => ({ ...prev, salary_type: 3 }))}
                                        className="text-blue-600"
                                    />
                                    <span className="text-sm">Negotiable</span>
                                </label>
                            </div>

                            {(formData.salary_type === 1 || formData.salary_type === 2) && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {formData.salary_type === 1 ? 'Salary (₹)' : 'Min Salary (₹)'} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            name="min_salary"
                                            value={formData.min_salary}
                                            onChange={handleInputChange}
                                            placeholder="25000"
                                            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.min_salary ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        {formErrors.min_salary && <p className="text-red-500 text-xs mt-1">{formErrors.min_salary}</p>}
                                    </div>
                                    {formData.salary_type === 2 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Max Salary (₹) <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="max_salary"
                                                value={formData.max_salary}
                                                onChange={handleInputChange}
                                                placeholder="45000"
                                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.max_salary ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {formErrors.max_salary && <p className="text-red-500 text-xs mt-1">{formErrors.max_salary}</p>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Checkboxes */}
                        <div className="flex gap-6 pt-4 border-t">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="is_verified"
                                    checked={formData.is_verified === 1}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_verified: e.target.checked ? 1 : 0 }))}
                                    className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-700">Verified Job</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="urgentHiring"
                                    checked={formData.urgentHiring === 1}
                                    onChange={(e) => setFormData(prev => ({ ...prev, urgentHiring: e.target.checked ? 1 : 0 }))}
                                    className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-700">Urgent Hiring</span>
                            </label>
                            {isEdit && (
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="status"
                                        checked={formData.status === 1}
                                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 1 : 0 }))}
                                        className="rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-700">Active</span>
                                </label>
                            )}
                        </div>
                    </div>
                );

            case 'experience':
                return (
                    <div className="space-y-6">
                        {/* Experience Level */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
                            <div className="flex gap-4 mb-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="exp_level"
                                        value={1}
                                        checked={formData.exp_level === 1}
                                        onChange={() => setFormData(prev => ({ ...prev, exp_level: 1, min_exp: '', max_exp: '' }))}
                                        className="text-blue-600"
                                    />
                                    <span className="text-sm">Fresher</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="exp_level"
                                        value={2}
                                        checked={formData.exp_level === 2}
                                        onChange={() => setFormData(prev => ({ ...prev, exp_level: 2, max_exp: '' }))}
                                        className="text-blue-600"
                                    />
                                    <span className="text-sm">Minimum Experience</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="exp_level"
                                        value={3}
                                        checked={formData.exp_level === 3}
                                        onChange={() => setFormData(prev => ({ ...prev, exp_level: 3 }))}
                                        className="text-blue-600"
                                    />
                                    <span className="text-sm">Experience Range</span>
                                </label>
                            </div>

                            {(formData.exp_level === 2 || formData.exp_level === 3) && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {formData.exp_level === 2 ? 'Min Experience (years)' : 'Min Experience (years)'} <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            name="min_exp"
                                            value={formData.min_exp}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.min_exp ? 'border-red-500' : 'border-gray-300'}`}
                                        >
                                            <option value="">Select Year</option>
                                            {Array.from({ length: 20 }, (_, i) => i + 1).map((year) => (
                                                <option key={year} value={year}>{year}{year === 20 ? '+' : ''} year{year > 1 ? 's' : ''}</option>
                                            ))}
                                        </select>
                                        {formErrors.min_exp && <p className="text-red-500 text-xs mt-1">{formErrors.min_exp}</p>}
                                    </div>
                                    {formData.exp_level === 3 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Max Experience (years) <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                name="max_exp"
                                                value={formData.max_exp}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.max_exp ? 'border-red-500' : 'border-gray-300'}`}
                                            >
                                                <option value="">Select Year</option>
                                                {Array.from({ length: 20 }, (_, i) => i + 1).map((year) => (
                                                    <option key={year} value={year}>{year}{year === 20 ? '+' : ''} year{year > 1 ? 's' : ''}</option>
                                                ))}
                                            </select>
                                            {formErrors.max_exp && <p className="text-red-500 text-xs mt-1">{formErrors.max_exp}</p>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Experience Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-blue-800 mb-2">Experience Level Summary</h4>
                            <p className="text-sm text-blue-700">
                                {formData.exp_level === 1 && 'This job is suitable for freshers with no prior experience required.'}
                                {formData.exp_level === 2 && formData.min_exp && `Candidates need minimum ${formData.min_exp} year${Number(formData.min_exp) > 1 ? 's' : ''} of experience.`}
                                {formData.exp_level === 2 && !formData.min_exp && 'Please select minimum experience required.'}
                                {formData.exp_level === 3 && formData.min_exp && formData.max_exp && `Candidates need ${formData.min_exp} to ${formData.max_exp} years of experience.`}
                                {formData.exp_level === 3 && (!formData.min_exp || !formData.max_exp) && 'Please select experience range.'}
                            </p>
                        </div>
                    </div>
                );

            case 'description':
                return (
                    <div className="space-y-6">
                        {/* Job Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Job Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={12}
                                placeholder="Enter detailed job description including responsibilities, requirements, benefits, etc..."
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.description ? 'border-red-500' : 'border-gray-300'}`}
                            />
                            {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
                            <p className="text-xs text-gray-500 mt-1">{formData.description.length} characters</p>
                        </div>

                        {/* Tips */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-800 mb-2">Tips for a great job description:</h4>
                            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                                <li>Clearly describe the role and responsibilities</li>
                                <li>List required qualifications and skills</li>
                                <li>Mention any benefits or perks</li>
                                <li>Include working hours and location details</li>
                                <li>Add application deadline if applicable</li>
                            </ul>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center gap-4">
                    {/* Left Side - Icon, Title with Badge */}
                    <div className="flex items-center gap-3" style={{ flex: '0 0 25%' }}>
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <Briefcase className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Jobs</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {pagination.totalItems.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">Manage job postings for this school</p>
                        </div>
                    </div>

                    {/* Right Side - Search and Filters */}
                    <div className="flex items-center gap-3 justify-end" style={{ flex: '0 0 75%' }}>
                        <div className="relative" style={{ width: '250px' }}>
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search jobs..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Status</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                        </select>

                        {/* <Button
                            onClick={handleOpenPostJobModal}
                            className="flex items-center gap-2 bg-brandColor hover:bg-brandColor/90 text-white"
                        >
                            <Plus size={18} />
                            Post New Job
                        </Button> */}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 mt-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('title')}
                                >
                                    Job Title {sortColumn === 'title' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Category
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Salary
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Job Type
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Applications
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('created_at')}
                                >
                                    Posted Date {sortColumn === 'created_at' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('status')}
                                >
                                    Status {sortColumn === 'status' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                        Loading jobs...
                                    </td>
                                </tr>
                            ) : jobs.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                        No jobs found for this school
                                    </td>
                                </tr>
                            ) : (
                                jobs.map((job, index) => (
                                    <tr
                                        key={job.id}
                                        className={`hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                        onClick={() => handleJobClick(job)}
                                    >
                                        <td className="px-4 py-2 text-sm text-gray-800 font-medium">
                                            <div className="flex items-center gap-2">
                                                {truncateText(job.title, 50)}
                                                {job.urgentHiring === 1 && (
                                                    <span className="px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700">Urgent</span>
                                                )}
                                                {job.is_verified === 1 && (
                                                    <CheckCircle size={14} className="text-green-600" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                                {job.jobCategory?.name || '-'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {getSalaryDisplay(job)}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {getJobTypeDisplay(job.jobType)}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            <div className="flex items-center gap-2">
                                                <Users size={14} />
                                                <span className="font-medium">{job.counts?.applications || 0}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                            {formatDate(job.created_at)}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            {getStatusBadge(job.status)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                    Showing {pagination.totalItems === 0 ? 0 : ((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                    {pagination.totalItems} results
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronsLeft size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages || 1}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                    >
                        <ChevronRight size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                    >
                        <ChevronsRight size={16} />
                    </Button>
                </div>
            </div>

            {/* Job Detail Modal */}
            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title="Job Details"
                subtitle={selectedJob ? `Job #${selectedJob.id}` : ''}
                icon={<div className="text-brandColor"><Briefcase size={24} /></div>}
                width="700px"
            >
                {selectedJob && (
                    <>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Job Info */}
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Job Title</label>
                                    <p className="text-lg font-semibold text-gray-900 mt-1">{selectedJob.title}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Category</label>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {selectedJob.jobCategory?.name || '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Status</label>
                                        <div className="mt-1">{getStatusBadge(selectedJob.status)}</div>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Job Type</label>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {getJobTypeDisplay(selectedJob.jobType)}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Salary</label>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {getSalaryDisplay(selectedJob)}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Posted Date</label>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {formatDate(selectedJob.created_at)}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Deadline</label>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {selectedJob.deadline ? formatDate(selectedJob.deadline) : '-'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Application Stats */}
                            <div className="border-t pt-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3">Application Statistics</h4>
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                                        <div className="text-2xl font-bold text-blue-600">{selectedJob.counts?.applications || 0}</div>
                                        <div className="text-xs text-gray-600">Total</div>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-3 text-center">
                                        <div className="text-2xl font-bold text-green-600">{selectedJob.counts?.shortlisted || 0}</div>
                                        <div className="text-xs text-gray-600">Shortlisted</div>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-3 text-center">
                                        <div className="text-2xl font-bold text-red-600">{selectedJob.counts?.rejected || 0}</div>
                                        <div className="text-xs text-gray-600">Rejected</div>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                                        <div className="text-2xl font-bold text-purple-600">{selectedJob.counts?.views || 0}</div>
                                        <div className="text-xs text-gray-600">Views</div>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            {selectedJob.description && (
                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Job Description</h4>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedJob.description}</p>
                                </div>
                            )}
                        </div>

                        {/* Fixed Footer */}
                        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                            <div className="flex gap-3 justify-between">
                                <Button
                                    variant={selectedJob.status === 1 ? 'outline' : 'primary'}
                                    onClick={() => handleToggleStatus(selectedJob.status)}
                                    leftIcon={selectedJob.status === 1 ? <EyeOff size={16} /> : <Eye size={16} />}
                                    className={selectedJob.status === 1 ? 'border-red-300 text-red-600 hover:bg-red-50' : ''}
                                >
                                    {selectedJob.status === 1 ? 'Deactivate Job' : 'Activate Job'}
                                </Button>
                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            handleCloseModal();
                                            handleOpenEditJobModal(selectedJob.id);
                                        }}
                                        leftIcon={<Edit size={16} />}
                                    >
                                        Edit Job
                                    </Button>
                                    <Button variant="outline" onClick={handleCloseModal}>
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </SettingsSlideModal>

            {/* Post Job Modal with Tabs */}
            <SettingsSlideModal
                isOpen={isPostJobModalOpen}
                onClose={handleClosePostJobModal}
                title="Post New Job"
                subtitle="Create a new job posting for this school"
                icon={<div className="text-brandColor"><Plus size={24} /></div>}
                width="800px"
            >
                <form onSubmit={handleSubmitJob} className="flex flex-col h-full">
                    {/* Tabs */}
                    <div className="border-b border-gray-200 px-6">
                        <div className="flex gap-1">
                            {JOB_FORM_TABS.map((tab, index) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`relative px-4 py-3 text-sm font-medium transition-colors ${
                                        activeTab === tab.id
                                            ? 'text-brandColor border-b-2 border-brandColor'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                            activeTab === tab.id
                                                ? 'bg-brandColor text-white'
                                                : tabHasErrors(tab.id)
                                                    ? 'bg-red-100 text-red-600'
                                                    : 'bg-gray-100 text-gray-600'
                                        }`}>
                                            {index + 1}
                                        </span>
                                        {tab.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {renderTabContent(false)}
                    </div>

                    {/* Fixed Footer */}
                    <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                        <div className="flex gap-3 justify-between">
                            <div className="flex gap-2">
                                {activeTab !== 'basic' && (
                                    <Button type="button" variant="outline" onClick={handlePrevTab}>
                                        Previous
                                    </Button>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <Button type="button" variant="outline" onClick={handleClosePostJobModal}>
                                    Cancel
                                </Button>
                                {activeTab !== 'description' ? (
                                    <Button type="button" onClick={handleNextTab} className="bg-brandColor hover:bg-brandColor/90 text-white">
                                        Next
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        disabled={isSubmittingJob}
                                        className="bg-brandColor hover:bg-brandColor/90 text-white"
                                    >
                                        {isSubmittingJob ? 'Posting...' : 'Post Job'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </SettingsSlideModal>

            {/* Edit Job Modal with Tabs */}
            <SettingsSlideModal
                isOpen={isEditJobModalOpen}
                onClose={handleCloseEditJobModal}
                title="Edit Job"
                subtitle={editJobId ? `Job #${editJobId}` : ''}
                icon={<div className="text-brandColor"><Edit size={24} /></div>}
                width="800px"
            >
                {isLoadingEditJob ? (
                    <div className="flex items-center justify-center h-64">
                        <p className="text-gray-500">Loading job details...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmitEditJob} className="flex flex-col h-full">
                        {/* Tabs */}
                        <div className="border-b border-gray-200 px-6">
                            <div className="flex gap-1">
                                {JOB_FORM_TABS.map((tab, index) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`relative px-4 py-3 text-sm font-medium transition-colors ${
                                            activeTab === tab.id
                                                ? 'text-brandColor border-b-2 border-brandColor'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                                activeTab === tab.id
                                                    ? 'bg-brandColor text-white'
                                                    : tabHasErrors(tab.id)
                                                        ? 'bg-red-100 text-red-600'
                                                        : 'bg-gray-100 text-gray-600'
                                            }`}>
                                                {index + 1}
                                            </span>
                                            {tab.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {renderTabContent(true)}
                        </div>

                        {/* Fixed Footer */}
                        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                            <div className="flex gap-3 justify-between">
                                <div className="flex gap-2">
                                    {activeTab !== 'basic' && (
                                        <Button type="button" variant="outline" onClick={handlePrevTab}>
                                            Previous
                                        </Button>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <Button type="button" variant="outline" onClick={handleCloseEditJobModal}>
                                        Cancel
                                    </Button>
                                    {activeTab !== 'description' ? (
                                        <Button type="button" onClick={handleNextTab} className="bg-brandColor hover:bg-brandColor/90 text-white">
                                            Next
                                        </Button>
                                    ) : (
                                        <Button
                                            type="submit"
                                            disabled={isSubmittingJob}
                                            className="bg-brandColor hover:bg-brandColor/90 text-white"
                                        >
                                            {isSubmittingJob ? 'Updating...' : 'Update Job'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </SettingsSlideModal>
        </div>
    );
};

export default JobList;